import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { services } from "@/lib/services-data";
import { locations } from "@/lib/locations-data";
import { business } from "@/lib/business-data";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import BrandLogo from "@/components/BrandLogo";

type NavLink = { label: string; href: string; dropdown?: "services" | "locations" };

const navLinks: NavLink[] = [
  { label: "Home", href: "/" },
  { label: "Services", href: "/services", dropdown: "services" },
  { label: "Locations", href: "/locations", dropdown: "locations" },
  { label: "Gallery", href: "/gallery" },
  { label: "Blog", href: "/blog" },
  { label: "About", href: "/about" },
];

const Navbar = () => {
  const { openQuoteForm } = useQuoteForm();
  const [open, setOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileLocationsOpen, setMobileLocationsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout>>();
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openDropdown = (id: string) => {
    clearTimeout(hoverTimeout.current);
    setActiveDropdown(id);
  };
  const closeDropdown = () => {
    hoverTimeout.current = setTimeout(() => setActiveDropdown(null), 180);
  };

  const mobileMenu = (
    <>
      <div
        className={`fixed inset-0 bg-black/55 backdrop-blur-sm z-[998] md:hidden transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />
      <div
        id="mobile-menu"
        aria-label="Mobile navigation"
        aria-hidden={!open}
        {...(open ? {} : { inert: "" })}
        className={`fixed top-0 left-0 h-full w-[19rem] max-w-[85vw] bg-card z-[999] md:hidden transform transition-transform duration-300 ease-in-out shadow-2xl flex flex-col ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <Link to="/" className="flex items-center" onClick={() => setOpen(false)}>
            <BrandLogo height={44} className="h-11 w-auto" />
          </Link>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="p-2 rounded-full text-foreground/70 hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <ul className="flex-1 overflow-y-auto scrollbar-slim flex flex-col px-4 py-4 gap-0.5">
          {navLinks.map((link) =>
            link.dropdown === "services" ? (
              <li key={link.href}>
                <div className="flex items-center justify-between">
                  <Link
                    to={link.href}
                    onClick={() => setOpen(false)}
                    className="flex-1 py-3 font-heading text-lg font-semibold text-foreground/85 hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                  <button
                    onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                    className="p-2 text-foreground/70 rounded-full hover:bg-muted transition-colors"
                    aria-label="Toggle services submenu"
                    aria-expanded={mobileServicesOpen}
                  >
                    <ChevronDown className={`h-4 w-4 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
                  </button>
                </div>
                {mobileServicesOpen && (
                  <ul className="pl-3 pb-2 space-y-0.5 border-l border-border ml-1">
                    {services.map((s) => (
                      <li key={s.slug}>
                        <Link
                          to={`/services/${s.slug}`}
                          onClick={() => setOpen(false)}
                          className="block py-2 pl-3 text-sm text-foreground/75 hover:text-primary transition-colors"
                        >
                          {s.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ) : link.dropdown === "locations" ? (
              <li key={link.href}>
                <div className="flex items-center justify-between">
                  <Link
                    to={link.href}
                    onClick={() => setOpen(false)}
                    className="flex-1 py-3 font-heading text-lg font-semibold text-foreground/85 hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                  <button
                    onClick={() => setMobileLocationsOpen(!mobileLocationsOpen)}
                    className="p-2 text-foreground/70 rounded-full hover:bg-muted transition-colors"
                    aria-label="Toggle locations submenu"
                    aria-expanded={mobileLocationsOpen}
                  >
                    <ChevronDown className={`h-4 w-4 transition-transform ${mobileLocationsOpen ? "rotate-180" : ""}`} />
                  </button>
                </div>
                {mobileLocationsOpen && (
                  <ul className="pl-3 pb-2 space-y-0.5 border-l border-border ml-1">
                    {locations.map((loc) => (
                      <li key={loc.slug}>
                        <Link
                          to={`/locations/${loc.slug}`}
                          onClick={() => setOpen(false)}
                          className="block py-2 pl-3 text-sm text-foreground/75 hover:text-primary transition-colors"
                        >
                          {loc.name}, {loc.state}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ) : (
              <li key={link.href}>
                <Link
                  to={link.href}
                  onClick={() => setOpen(false)}
                  className="block py-3 font-heading text-lg font-semibold text-foreground/85 hover:text-primary transition-colors"
                >
                  {link.label}
                </Link>
              </li>
            )
          )}
        </ul>

        <div className="shrink-0 border-t border-border p-4 space-y-2.5">
          <Button
            className="w-full gap-2"
            onClick={() => {
              openQuoteForm();
              setOpen(false);
            }}
          >
            Get Free Estimate
          </Button>
          <Button asChild variant="outline" className="w-full gap-2">
            <a href={business.phoneHref}>
              <Phone className="h-4 w-4 text-primary" /> {business.phone}
            </a>
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <>
      <nav className={`site-header ${scrolled ? "site-header--scrolled" : ""}`} aria-label="Primary">
        <div className="header-shell">
          <div className="header-inner container-max flex items-center justify-between px-4 sm:px-6 py-2.5">
            <Link to="/" className="header-logo flex items-center" aria-label={`${business.name} — home`}>
              <BrandLogo height={56} className="h-12 lg:h-14 w-auto" />
            </Link>

            <ul className="hidden md:flex items-center gap-0.5 lg:gap-1.5">
              {navLinks.map((link) =>
                link.dropdown ? (
                  <li
                    key={link.href}
                    className="relative"
                    onMouseEnter={() => openDropdown(link.dropdown as string)}
                    onMouseLeave={closeDropdown}
                  >
                    <Link
                      to={link.href}
                      data-active={location.pathname.startsWith(link.href)}
                      aria-current={location.pathname.startsWith(link.href) ? "page" : undefined}
                      className="nav-pill font-heading text-[15px] font-medium tracking-wide text-foreground/85 hover:text-primary inline-flex items-center gap-1"
                    >
                      {link.label}
                      <ChevronDown
                        className={`h-3.5 w-3.5 transition-transform ${
                          activeDropdown === link.dropdown ? "rotate-180" : ""
                        }`}
                      />
                    </Link>

                    {activeDropdown === link.dropdown && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-[70]">
                        {/* Compact two-column link list — no scrollbar, no mega menu. */}
                        <div
                          className={`header-dropdown p-2 grid grid-cols-2 gap-x-1 ${
                            link.dropdown === "services" ? "w-[30rem]" : "w-[24rem]"
                          }`}
                        >
                          {(link.dropdown === "services" ? services : locations).map((item) => {
                            const isService = "title" in item;
                            const to = isService ? `/services/${item.slug}` : `/locations/${item.slug}`;
                            const label = isService ? item.title : `${item.name}, ${item.state}`;
                            return (
                              <Link
                                key={item.slug}
                                to={to}
                                className="block rounded-lg px-3 py-2 text-sm font-medium text-popover-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                                onClick={() => setActiveDropdown(null)}
                              >
                                {label}
                              </Link>
                            );
                          })}
                          <Link
                            to={link.href}
                            className="col-span-2 mt-1 block rounded-lg bg-primary/8 px-3 py-2 text-center text-sm font-bold text-primary hover:bg-primary/15 transition-colors"
                            onClick={() => setActiveDropdown(null)}
                          >
                            View all {link.label.toLowerCase()} →
                          </Link>
                        </div>
                      </div>
                    )}
                  </li>
                ) : (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      data-active={
                        link.href === "/" ? location.pathname === "/" : location.pathname.startsWith(link.href)
                      }
                      aria-current={
                        (link.href === "/" ? location.pathname === "/" : location.pathname.startsWith(link.href))
                          ? "page"
                          : undefined
                      }
                      className="nav-pill font-heading text-[15px] font-medium tracking-wide text-foreground/85 hover:text-primary"
                    >
                      {link.label}
                    </Link>
                  </li>
                )
              )}
              <li className="hidden lg:block pl-2">
                <Button size="sm" onClick={openQuoteForm} className="rounded-full px-5 btn-glow">
                  Get Free Estimate
                </Button>
              </li>
            </ul>

            <button
              type="button"
              onClick={() => setOpen(!open)}
              className="md:hidden text-foreground p-2 rounded-full hover:bg-primary/10 transition-colors"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="mobile-menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </nav>
      {createPortal(mobileMenu, document.body)}
    </>
  );
};

export default Navbar;
